import { api } from '@/api/client';
import type {
  AuthUser,
  ChangePasswordPayload,
  LoginPayload,
  RegisterPayload,
  ResetPasswordPayload,
} from '@/types/auth';

/** Query keys for the auth domain, kept beside the fetchers. */
export const authKeys = {
  user: ['auth', 'user'] as const,
};

/**
 * Loads the signed-in user, or null when nobody is.
 *
 * The endpoint answers "who am I?" and returns `data: null` for a guest —
 * a successful answer, not an error. That keeps the anonymous case off the
 * error path and out of the browser console.
 */
export function fetchAuthUser(): Promise<AuthUser | null> {
  return api.get<AuthUser | null>('/auth/user');
}

export function register(payload: RegisterPayload): Promise<AuthUser> {
  return api.post<AuthUser>('/auth/register', payload);
}

export function login(payload: LoginPayload): Promise<AuthUser> {
  return api.post<AuthUser>('/auth/login', payload);
}

export function logout(): Promise<null> {
  return api.post<null>('/auth/logout');
}

export function sendPasswordResetLink(email: string): Promise<null> {
  return api.post<null>('/auth/forgot-password', { email });
}

export function resetPassword(payload: ResetPasswordPayload): Promise<null> {
  return api.post<null>('/auth/reset-password', payload);
}

export function changePassword(payload: ChangePasswordPayload): Promise<null> {
  return api.put<null>('/auth/password', payload);
}

export function resendVerificationEmail(): Promise<null> {
  return api.post<null>('/auth/email/resend');
}

/**
 * Completes email verification.
 *
 * The signed parameters arrive on the SPA route from the emailed link and are
 * forwarded to the API unchanged — the signature was computed over this API
 * URL, so it only validates if nothing was altered in transit.
 */
export function verifyEmail(params: {
  id: string;
  hash: string;
  expires: string;
  signature: string;
}): Promise<null> {
  const { id, hash, ...query } = params;

  return api.get<null>(`/auth/email/verify/${id}/${hash}`, query);
}
